import apiService from '../api'

export type OperationalCostStage = 'clearing' | 'storage_dispatch' | 'order_fulfillment'

export interface OperationalCost {
  id: string
  stage: OperationalCostStage
  category: string
  description?: string | null
  amount: number | string
  currency: string
  incurredAt: string
  shipmentId?: string | null
  contractId?: string | null
  clearingRecordId?: string | null
  loadOutId?: string | null
  orderFulfillmentId?: string | null
  warehouseId?: string | null
  peanutLotId?: string | null
  xeroInvoiceId?: string | null
  xeroSyncedAt?: string | null
  xeroPaidAt?: string | null
  createdAt: string
  updatedAt: string
  shipment?: { id: string; containerNumber?: string; buyContract?: string; saleContract?: string } | null
  clearingRecord?: { id: string; lotNumber?: string; status?: string } | null
  loadOut?: { id: string; lotNumber?: string; status?: string } | null
  orderFulfillment?: {
    id: string
    containerNumber?: string
    outcome?: string
    salesforceOrder?: { orderNumber?: string | null; accountName?: string | null; xeroInvoiceId?: string | null } | null
  } | null
  warehouse?: { id: string; name: string } | null
  peanutLot?: { id: string; lotNumber: string } | null
}

export interface OperationalCostsListResponse {
  operationalCosts: OperationalCost[]
  meta?: { total: number; page: number; limit: number; totalPages: number }
}

export interface CreateOperationalCostPayload {
  stage: OperationalCostStage
  category: string
  description?: string
  amount: number
  currency?: string
  incurredAt?: string
  shipmentId?: string
  contractId?: string
  clearingRecordId?: string
  loadOutId?: string
  orderFulfillmentId?: string
  warehouseId?: string
  peanutLotId?: string
}

export interface UpdateOperationalCostPayload {
  category?: string
  description?: string | null
  amount?: number
  currency?: string
  incurredAt?: string
}

const COST_CATEGORIES: Record<OperationalCostStage, string[]> = {
  clearing: ['customs_duties', 'clearing_agent_fees', 'port_documentation'],
  storage_dispatch: ['warehouse_storage', 'yard_silo', 'transporter'],
  order_fulfillment: ['repositioning', 'rerouting', 'ncr_handling_penalty'],
}

export function getCategoriesForStage(stage: OperationalCostStage): string[] {
  return COST_CATEGORIES[stage] ?? []
}

export async function getOperationalCosts(params?: {
  page?: number
  limit?: number
  stage?: OperationalCostStage
  shipmentId?: string
  clearingRecordId?: string
  loadOutId?: string
  orderFulfillmentId?: string
  warehouseId?: string
}): Promise<OperationalCostsListResponse> {
  const qs = new URLSearchParams()
  if (params?.page != null) qs.set('page', String(params.page))
  if (params?.limit != null) qs.set('limit', String(params.limit))
  if (params?.stage) qs.set('stage', params.stage)
  if (params?.shipmentId) qs.set('shipmentId', params.shipmentId)
  if (params?.clearingRecordId) qs.set('clearingRecordId', params.clearingRecordId)
  if (params?.loadOutId) qs.set('loadOutId', params.loadOutId)
  if (params?.orderFulfillmentId) qs.set('orderFulfillmentId', params.orderFulfillmentId)
  if (params?.warehouseId) qs.set('warehouseId', params.warehouseId)
  const url = `/operational-costs${qs.toString() ? '?' + qs.toString() : ''}`
  const res = await apiService.get<{ success: boolean; data: OperationalCostsListResponse; meta?: unknown }>(url)
  return (res as any)?.data ?? res
}

export async function getOperationalCostById(id: string): Promise<OperationalCost> {
  const res = await apiService.get<{ success: boolean; data: OperationalCost }>(`/operational-costs/${id}`)
  return (res as any)?.data ?? res
}

export async function createOperationalCost(payload: CreateOperationalCostPayload): Promise<OperationalCost> {
  const res = await apiService.post<{ success: boolean; data: OperationalCost }>('/operational-costs', payload)
  return (res as any)?.data ?? res
}

export async function updateOperationalCost(id: string, payload: UpdateOperationalCostPayload): Promise<OperationalCost> {
  const res = await apiService.put<{ success: boolean; data: OperationalCost }>(`/operational-costs/${id}`, payload)
  return (res as any)?.data ?? res
}

export async function deleteOperationalCost(id: string): Promise<void> {
  await apiService.delete(`/operational-costs/${id}`)
}

export type SupplierCostImportResult = {
  parsed: number
  inserted: number
  updated: number
  linkedToOrders: number
  unlinked: number
  since: string
}

export interface FinanceCollectedLot {
  id: string
  product?: string | null
  booking?: string | null
  factory?: string | null
  supplierInvoice?: string | null
  bl?: string | null
  containerNumber?: string | null
  warehouseReceiptDate?: string | null
  lot?: string | null
  customer?: string | null
  kcgInvoice?: string | null
  deliveryDate?: string | null
  invoiceDate?: string | null
  bigBags?: number | string | null
  tn?: number | string | null
  importZar?: number | string | null
  status?: string | null
  amountZar?: number | string | null
  dateCollected?: string | null
  invoiceCostUsd?: number | string | null
  totalInR?: number | string | null
  exchangeRate?: number | string | null
  exporter?: string | null
  supplierPayment?: string | null
  supplierPaymentDate?: string | null
  tons?: number | string | null
  orderFulfillmentId?: string | null
  orderFulfillment?: {
    id: string
    containerNumber?: string | null
    salesforceOrder?: { orderNumber?: string | null; xeroInvoiceId?: string | null } | null
  } | null
}

export interface FinanceCollectedLotsResponse {
  lots: FinanceCollectedLot[]
  meta?: { total: number; page: number; limit: number; totalPages: number }
}

export type CollectedLotImportResult = {
  sheet: string
  parsed: number
  skipped: number
  inserted: number
  updated: number
  linkedToOrders: number
  unlinked: number
  through: string
}

export async function getFinanceCollectedLots(params?: {
  page?: number
  limit?: number
  status?: string
  search?: string
}): Promise<FinanceCollectedLotsResponse> {
  const qs = new URLSearchParams()
  if (params?.page != null) qs.set('page', String(params.page))
  if (params?.limit != null) qs.set('limit', String(params.limit))
  if (params?.status) qs.set('status', params.status)
  if (params?.search) qs.set('search', params.search)
  const url = `/operational-costs/lots${qs.toString() ? '?' + qs.toString() : ''}`
  const res = await apiService.get<{ success: boolean; data: FinanceCollectedLotsResponse }>(url)
  return (res as any)?.data ?? res
}

export async function importFinanceCollectedLots(
  file: File,
  through = '2026-09-30'
): Promise<CollectedLotImportResult> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('through', through)
  const res = await apiService.instance.post<{
    success?: boolean
    data?: CollectedLotImportResult
  }>('/operational-costs/import/lots', formData, {
    timeout: 180_000,
    transformRequest: [
      (data: unknown, headers: Record<string, string>) => {
        if (data instanceof FormData) delete headers['Content-Type']
        return data
      },
    ],
  })
  return res.data.data as CollectedLotImportResult
}

export async function importSupplierOperationalCosts(
  file: File,
  since = '2026-04-01'
): Promise<SupplierCostImportResult> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('since', since)
  const res = await apiService.instance.post<{
    success?: boolean
    data?: SupplierCostImportResult
  }>('/operational-costs/import/suppliers', formData, {
    timeout: 180_000,
    transformRequest: [
      (data: unknown, headers: Record<string, string>) => {
        if (data instanceof FormData) delete headers['Content-Type']
        return data
      },
    ],
  })
  return res.data.data as SupplierCostImportResult
}
